// © 2025 Camp Promethean, LLC. All Rights Reserved.

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { notificationsService, NotificationRecord } from '@/services/notifications';
import { useAuth } from './AuthContext';

export type NotificationType = 'urgent' | 'info' | 'success' | 'warning';

export interface Notification {
  id?: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp?: Date;
  read?: boolean;
  source: string;
  actionUrl?: string;
}

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  removeNotification: (id: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  isLoading: boolean;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { profile } = useAuth();

  const loadNotifications = useCallback(async () => {
    if (!profile?.id) return;
    
    try {
      setIsLoading(true);
      const records = await notificationsService.getNotifications();
      const count = await notificationsService.getUnreadCount();
      
      setNotifications(records.map(record => ({
        id: record.id,
        type: record.type,
        title: record.title,
        message: record.message,
        timestamp: new Date(record.created_at),
        read: record.read,
        source: record.source,
        actionUrl: record.action_url,
      })));
      
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    loadNotifications();
    
    // Set up real-time subscription
    const subscription = notificationsService.subscribeToNotifications(() => {
      loadNotifications();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [loadNotifications]);

  const addNotification = useCallback(async (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    try {
      await notificationsService.createNotification({
        type: notification.type,
        title: notification.title,
        message: notification.message,
        source: notification.source,
        action_url: notification.actionUrl,
        read: false,
      });

      // Show toast for urgent notifications
      if (notification.type === 'urgent') {
        toast.error(notification.title, {
          description: notification.message,
        });
      }
    } catch (error) {
      console.error('Error adding notification:', error);
    }
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationsService.markAsRead(id);
      await loadNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [loadNotifications]);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsService.markAllAsRead();
      await loadNotifications();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [loadNotifications]);

  const removeNotification = useCallback(async (id: string) => {
    try {
      await notificationsService.deleteNotification(id);
      await loadNotifications();
    } catch (error) {
      console.error('Error removing notification:', error);
    }
  }, [loadNotifications]);

  const clearAllNotifications = useCallback(async () => {
    try {
      await notificationsService.deleteOldNotifications(0);
      await loadNotifications();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }, [loadNotifications]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAllNotifications,
        isLoading,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
} 